import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { SignInPage, SignInProviderConfig } from '@backstage/core-components';
import {
  configApiRef,
  githubAuthApiRef,
  useApi,
  SignInPageProps,
} from '@backstage/core-plugin-api';

// A SignInPage cannot be added by configuration — it is compiled into the app
// bundle. That is the whole reason this repo's image exists instead of the
// upstream `ghcr.io/backstage/backstage` one, which only ever offers guest.

const githubProvider: SignInProviderConfig = {
  id: 'github-auth-provider',
  title: 'GitHub',
  message: 'Sign in with your github.ibm.com account',
  apiRef: githubAuthApiRef,
};

// `guest` and `custom` are strings the SignInPage understands directly; every
// other entry is a provider config.
const knownProviders: Record<string, SignInProviderConfig | 'guest'> = {
  guest: 'guest',
  github: githubProvider,
};

// Which buttons to show is config, not code, so `yarn start` works without a
// GitHub OAuth app while the deployed image offers GitHub only. The key is
// declared frontend-visible in packages/app/config.d.ts; anything not declared
// there is stripped out of the config the browser receives.
//
// The default is deliberately `['github']` and not `['guest', 'github']`: if
// the key ever fails to reach the frontend, the failure mode should be a
// portal nobody can get into, not one everybody can.
const defaultProviders = ['github'];

const ConfiguredSignInPage = (props: SignInPageProps) => {
  const configApi = useApi(configApiRef);
  const ids =
    configApi.getOptionalStringArray('app.signInProviders') ?? defaultProviders;

  const providers = ids
    .map(id => knownProviders[id])
    .filter((p): p is SignInProviderConfig | 'guest' => Boolean(p));

  return (
    <SignInPage
      {...props}
      title="Sign in to Product Workbench"
      align="center"
      providers={providers.length > 0 ? providers : [githubProvider]}
    />
  );
};

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => ConfiguredSignInPage,
  },
});

// pluginId 'app' is required — SignInPageBlueprint replaces an extension the
// app itself owns. It shares that pluginId with navModule, which is fine:
// extensions are keyed by their own id, and these two do not collide.
export const authModule = createFrontendModule({
  pluginId: 'app',
  extensions: [signInPage],
});
